import {invariant} from '@raison/toolbox';
import React from 'react';
import {useParams} from 'react-router';
import {GateComponent, mergeGates} from '~/library/navigation';

const joinPaths = (pathA: string, pathB: string) => pathA.replace(/\/?$/, pathB.replace(/^\/?/, '/'));

function ParamsProvider({render}: {render: (params: any) => React.JSX.Element}) {
    const params = useParams();
    const element = render(params);
    return element;
}

export const defineRoutes = createRoutesBuilder({
    defineRoute: (path, render) => {
        return {
            path,
            element: React.createElement(ParamsProvider, {render}),
        };
    },
    mergeRoutes: (a, b) => {
        const merged = {...a, ...b};
        if (a.path && b.path) {
            merged.path = joinPaths(a.path, b.path);
        }
        if (a.Gate && b.Gate) {
            merged.Gate = mergeGates(a.Gate, b.Gate);
        }
        return merged;
    },
});

export type RouteRegistrar = DefineRouteMethod;

interface DefineRoute {
    <P extends string>(path: P, renderFunc: (params: ExtractRouteParams<P, string>) => React.JSX.Element): RouteConfig;
}

interface MergeRoutes {
    <T>(leftRoute: RouteConfigEx, rightRoute: RouteConfigEx & T): RouteConfigEx & T;
}

interface DefineRouteMethod extends DefineRoute {
    with: (extras: RouteConfigEx) => DefineRouteMethod;
}

interface GroupRoutesMethod {
    (extras: RouteConfigEx, scope: () => void): void;
}

type RouteConfig = {
    path: string;
    element: React.JSX.Element;
    Gate?: GateComponent;
};
type RouteConfigEx = Partial<RouteConfig>;

export function createRoutesBuilder(params: {defineRoute: DefineRoute; mergeRoutes: MergeRoutes}) {
    const {defineRoute, mergeRoutes} = params;

    return function routesBuilder(scope: (tools: {route: DefineRouteMethod; group: GroupRoutesMethod}) => void) {
        const result: RouteConfig[] = [];

        let currentGroupExtras: RouteConfigEx = {};

        const group = (extras: RouteConfigEx, groupScope: () => void) => {
            const previousGroupExtras = currentGroupExtras;
            currentGroupExtras = mergeRoutes(currentGroupExtras, extras);
            groupScope();
            currentGroupExtras = previousGroupExtras;
        };

        const createPushMethod = (extras: RouteConfigEx) => {
            const baseMethod = ((path, render) => {
                invariant(isBuilding, "Can't add routes after routes builder completes");

                const route = defineRoute(path, render);

                const merged = mergeRoutes(currentGroupExtras, mergeRoutes(extras, route));
                result.push(merged);

                return route;
            }) as DefineRouteMethod;

            baseMethod.with = nextExtras => {
                return createPushMethod(mergeRoutes(extras, nextExtras));
            };

            return baseMethod;
        };

        let isBuilding = true;

        scope({
            route: createPushMethod({}),
            group,
        });

        isBuilding = false;

        return result.map(({Gate, ...route}) => ({
            ...route,
            handle: {Gate},
            element: route.element,
        }));
    };
}

// copied from react-router 5 definitions
type ExtractRouteOptionalParam<T extends string, U = string | number | boolean> = T extends `${infer Param}?`
    ? {[k in Param]?: U}
    : T extends `${infer Param}*`
      ? {[k in Param]?: U}
      : T extends `${infer Param}+`
        ? {[k in Param]: U}
        : {[k in T]: U};

type ExtractRouteParams<T extends string, U = string | number | boolean> = string extends T
    ? {[k in string]?: U}
    : T extends `${infer _Start}:${infer ParamWithOptionalRegExp}/${infer Rest}`
      ? ParamWithOptionalRegExp extends `${infer Param}(${infer _RegExp})${infer Modifier extends '?' | '+' | '*' | ''}`
          ? ExtractRouteOptionalParam<`${Param}${Modifier}`, U> & ExtractRouteParams<Rest, U>
          : ExtractRouteOptionalParam<ParamWithOptionalRegExp, U> & ExtractRouteParams<Rest, U>
      : T extends `${infer _Start}:${infer ParamWithOptionalRegExp}`
        ? ParamWithOptionalRegExp extends `${infer Param}(${infer _RegExp})${infer Modifier extends '?' | '+' | '*' | ''}`
            ? ExtractRouteOptionalParam<`${Param}${Modifier}`, U>
            : ExtractRouteOptionalParam<ParamWithOptionalRegExp, U>
        : {};
